/**
 * CraveTheory viewport entrance animations
 *
 * data-ct-animate          → reveal when section scrolls into view
 * data-ct-animate="eager"  → also ok to reveal immediately if already on screen (hero/header)
 *
 * Uses Horizon scroll-container intersection root so desktop `.page-wrapper`
 * scrolling (and Lenis) triggers reveals correctly.
 */
import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

const SELECTOR = '[data-ct-animate]';
const VISIBLE = 'ct-inview';
const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | null} */
let sharedIo = null;
/** @type {WeakSet<Element>} */
const observed = new WeakSet();

/** @param {HTMLElement} root */
const reveal = (root) => {
  if (root.classList.contains(VISIBLE)) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      root.classList.add(VISIBLE);
    });
  });
};

/** @param {HTMLElement} root */
const isEager = (root) => root.getAttribute('data-ct-animate') === 'eager';

function buildObserver() {
  if (sharedIo) {
    sharedIo.disconnect();
    sharedIo = null;
  }

  if (!('IntersectionObserver' in window)) return null;

  sharedIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = /** @type {HTMLElement} */ (entry.target);
        const eager = isEager(target);
        // Scroll-triggered sections need a real slice of the viewport
        if (!eager && entry.intersectionRatio < 0.12) return;
        reveal(target);
        sharedIo?.unobserve(target);
      });
    },
    {
      root: getIntersectionRoot(),
      threshold: [0, 0.05, 0.1, 0.12, 0.2, 0.35],
      // Slightly earlier trigger so Lenis lerp still feels snappy with reveals
      rootMargin: '0px 0px -8% 0px',
    }
  );

  return sharedIo;
}

/** @param {HTMLElement} root */
const watch = (root) => {
  if (root.dataset.ctAnimateReady === 'true' && observed.has(root)) return;
  root.dataset.ctAnimateReady = 'true';

  if (REDUCED.matches) {
    root.classList.add(VISIBLE);
    return;
  }

  if (!('IntersectionObserver' in window)) {
    reveal(root);
    return;
  }

  if (!sharedIo) buildObserver();
  const eager = isEager(root);

  sharedIo?.observe(root);
  observed.add(root);

  // Eager (hero/header): if already painted in view, start soon
  if (eager) {
    const rect = root.getBoundingClientRect();
    const vh = window.innerHeight || 800;
    if (rect.top < vh * 0.92 && rect.bottom > 0) {
      window.setTimeout(() => reveal(root), 80);
    }
  }

  // Safety only — never used as the intended trigger
  window.setTimeout(() => {
    if (!root.classList.contains(VISIBLE)) reveal(root);
  }, 12000);
};

const init = () => {
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) watch(el);
  });
};

const rebindRoot = () => {
  // Crossing squeeze breakpoint changes the IO root — rebuild and re-observe pending nodes
  buildObserver();
  document.querySelectorAll(SELECTOR).forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (el.classList.contains(VISIBLE)) return;
    el.dataset.ctAnimateReady = 'false';
    observed.delete(el);
    watch(el);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
window.addEventListener('pageshow', init);
scrollContainerMediaQuery.addEventListener('change', () => {
  requestAnimationFrame(rebindRoot);
});
